import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Filtros para la búsqueda de Control DUS
 */
export interface ControlDusFilter {
    page?: number;
    limit?: number;
    orderBy?: string;
    orderDirection?: 'ASC' | 'DESC';
    clienteId?: number;
    temporadaId?: number;
    nave?: string;
    dateFrom?: string;
    dateTo?: string;
    referenciaCliente?: string;
    contenedor?: string;
    dus?: string;
    booking?: string;
    etapa?: string;
    estado?: string;
    excludeCancelado?: boolean;
}

/**
 * Item individual de Control DUS
 */
export interface ControlDusItem {
    nroIE: number;
    clienteId?: number;
    cliente?: string;
    exportador?: string;
    refCliente?: string;
    contenedor?: string;
    plts?: number;
    dus?: string;
    sps?: number;
    isf?: Date;
    reserva?: string;
    tipoEmbarque?: string;
    nave?: string;
    viaje?: string;
    pol?: string;
    pod?: string;
    etd?: Date;
    eta?: Date;
    dias?: number;
    etapa?: string;
    estado: string;
    despacho?: string;
    estadoAduana: string;
    fechaAceptacion?: string;
    fechaLegalizacion?: string;
    prorroga: string;
    vencimiento?: string;
    diasVencimiento?: number;
    rangoVencimiento?: string;
    tipoAtraso: string;
    tipoAtrasoGrafico: string;
    comentarios: string;
    ejecutivo?: string;
    customsLoadReferenceStatusId?: number;
}

/**
 * Respuesta de lista de Control DUS
 */
export interface ControlDusResponse {
    data: ControlDusItem[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
    timestamp: string;
}

/**
 * Opciones de combo para filtros
 */
export interface ComboOption {
    value: string | number;
    label: string;
    count?: number;
}

/**
 * Interfaz para naves (igual que OS Agencia y Commons)
 */
export interface IVesselFilter {
    vesselId: number;
    vesselName: string;
}

/**
 * Interfaz para temporadas
 */
export interface TemporadaDto {
    seasonId: number;
    seasonName: string;
    seasonFrom?: string;
    seasonTo?: string;
}

/**
 * Respuesta de temporadas desde el backend
 */
export interface TemporadasResponse {
    data: TemporadaDto[];
    total: number;
    timestamp: string;
}

/**
 * Respuesta de combos (estructura genérica anterior)
 */
export interface CombosResponse {
    success: boolean;
    data: ComboOption[];
}

/**
 * Respuesta específica de etapas desde el backend
 */
export interface EtapasResponse {
    data: Array<{
        etapa: string;
        count: number;
    }>;
    total: number;
    timestamp: string;
}

/**
 * Respuesta específica de estados desde el backend
 */
export interface EstadosResponse {
    data: Array<{
        estado: string;
        count: number;
    }>;
    total: number;
    timestamp: string;
}

/**
 * Respuesta de estadísticas para gráficos
 */
export interface ChartStatisticsResponse {
    vencimiento: { rango: string; cantidad: number }[];
    prorroga: { prorroga: string; cantidad: number }[];
    atraso: { tipoAtraso: string; cantidad: number }[];
    timestamp: string;
}

/**
 * Item del Reporte de Guías de Despacho
 */
export interface DispatchGuideReportItem {
    referencia: string;
    cliente: string;
    exportador: string;
    guia: string;
    estadoGuia: string;
    bodega: string;
    patente: string;
    nave: string;
    booking: string;
    pol: string;
    etd: string | null;
    pod: string;
    consignatario: string;
    dus: string;
    contenedor: string;
    cantidadCntrs: number;
    tipoEmbarque: string;
    cantidadPallets: number;
    despacho: string | null;
    visacion: string | null;
    ingresoPuerto: string | null;
    stacking: string | null;
    liquidacion: string | null;
    salidaPuerto: string | null;
    estadiaPuerto: number | null;
    comentarios: string;
}

@Injectable({
    providedIn: 'root',
})
export class ControlDusService {
    private readonly apiUrl = `${environment.apiUrl}/v1/control-dus`;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private http: HttpClient
    ) {}

    /**
     * Obtener lista de Control DUS con filtros y paginación
     */
    getControlDusList(
        filters: ControlDusFilter
    ): Observable<ControlDusResponse> {
        let params = new HttpParams();

        // Agregar parámetros de paginación
        if (filters.page) {
            params = params.set('page', filters.page.toString());
        }
        if (filters.limit) {
            params = params.set('limit', filters.limit.toString());
        }

        // Agregar parámetros de ordenamiento
        if (filters.orderBy) {
            params = params.set('orderBy', filters.orderBy);
        }
        if (filters.orderDirection) {
            params = params.set('orderDirection', filters.orderDirection);
        }

        // Agregar filtros de búsqueda
        if (filters.clienteId) {
            params = params.set('clienteId', filters.clienteId.toString());
        }
        if (filters.temporadaId) {
            params = params.set('temporadaId', filters.temporadaId.toString());
        }
        if (filters.nave) {
            params = params.set('nave', filters.nave);
        }
        if (filters.dateFrom) {
            params = params.set('dateFrom', filters.dateFrom);
        }
        if (filters.dateTo) {
            params = params.set('dateTo', filters.dateTo);
        }
        if (filters.referenciaCliente) {
            params = params.set('referenciaCliente', filters.referenciaCliente);
        }
        if (filters.contenedor) {
            params = params.set('contenedor', filters.contenedor);
        }
        if (filters.dus) {
            params = params.set('dus', filters.dus);
        }
        if (filters.booking) {
            params = params.set('booking', filters.booking);
        }
        if (filters.etapa) {
            params = params.set('etapa', filters.etapa);
        }
        if (filters.estado) {
            params = params.set('estado', filters.estado);
        }

        return this.http.get<ControlDusResponse>(this.apiUrl, { params });
    }

    /**
     * Obtener estadísticas para gráficos con filtros
     * Los gráficos muestran datos agregados de todos los registros que coinciden con los filtros
     */
    getStatistics(
        filters: ControlDusFilter
    ): Observable<ChartStatisticsResponse> {
        let params = new HttpParams();

        // Agregar filtros de búsqueda (mismos que el listado, sin paginación)
        if (filters.clienteId) {
            params = params.set('clienteId', filters.clienteId.toString());
        }
        if (filters.temporadaId) {
            params = params.set('temporadaId', filters.temporadaId.toString());
        }
        if (filters.nave) {
            params = params.set('nave', filters.nave);
        }
        if (filters.dateFrom) {
            params = params.set('dateFrom', filters.dateFrom);
        }
        if (filters.dateTo) {
            params = params.set('dateTo', filters.dateTo);
        }
        if (filters.referenciaCliente) {
            params = params.set('referenciaCliente', filters.referenciaCliente);
        }
        if (filters.contenedor) {
            params = params.set('contenedor', filters.contenedor);
        }
        if (filters.dus) {
            params = params.set('dus', filters.dus);
        }
        if (filters.booking) {
            params = params.set('booking', filters.booking);
        }
        if (filters.etapa) {
            params = params.set('etapa', filters.etapa);
        }
        if (filters.estado) {
            params = params.set('estado', filters.estado);
        }
        if (filters.excludeCancelado) {
            params = params.set('excludeCancelado', 'true');
        }

        return this.http.get<ChartStatisticsResponse>(
            `${this.apiUrl}/statistics`,
            { params }
        );
    }

    /**
     * Obtener detalle de un DUS específico por nroIE
     */
    getControlDusDetalle(
        nroIE: number
    ): Observable<{ success: boolean; data: ControlDusItem }> {
        return this.http.get<ControlDusItem>(`${this.apiUrl}/${nroIE}`).pipe(
            map((data: ControlDusItem) => ({
                success: true,
                data: data,
            }))
        );
    }

    /**
     * Obtener lista de naves para el filtro
     */
    getVessels(): Observable<CombosResponse> {
        return this.http.get<CombosResponse>(`${this.apiUrl}/filters/vessels`);
    }

    /**
     * Obtener lista de etapas para el filtro
     * Normaliza el encoding de caracteres con tilde (? → ó, á, é, etc.)
     */
    getEtapas(): Observable<CombosResponse> {
        return this.http
            .get<EtapasResponse>(`${this.apiUrl}/filters/etapas`)
            .pipe(
                map((response) => ({
                    success: true,
                    data: response.data.map((item) => ({
                        value: this.normalizarEncoding(item.etapa),
                        label: this.normalizarEncoding(item.etapa),
                        count: item.count,
                    })),
                }))
            );
    }

    /**
     * Obtener lista de estados para el filtro
     * Normaliza el encoding de caracteres con tilde (? → ó, á, é, etc.)
     */
    getEstados(): Observable<CombosResponse> {
        return this.http
            .get<EstadosResponse>(`${this.apiUrl}/filters/estados`)
            .pipe(
                map((response) => ({
                    success: true,
                    data: response.data.map((item) => ({
                        value: this.normalizarEncoding(item.estado),
                        label: this.normalizarEncoding(item.estado),
                        count: item.count,
                    })),
                }))
            );
    }

    /**
     * Obtener lista de temporadas para el filtro
     */
    getTemporadas(): Observable<TemporadasResponse> {
        return this.http.get<TemporadasResponse>(
            `${this.apiUrl}/filters/seasons`
        );
    }

    /**
     * Obtener clientes con DUS visibles para el usuario autenticado.
     * Para consignatario: solo sus clientes. Para admin: todos.
     */
    getClientes(): Observable<{ clienteId: number; cliente: string }[]> {
        return this.http.get<{ clienteId: number; cliente: string }[]>(
            `${this.apiUrl}/filters/clientes`
        );
    }

    /**
     * Obtener consignatarios para el panel admin (solo admin/operator).
     */
    getConsignees(): Observable<{ consigneeId: number; consigneeShortName: string }[]> {
        return this.http.get<{ consigneeId: number; consigneeShortName: string }[]>(
            `${this.apiUrl}/filters/consignees`
        );
    }

    /**
     * Normaliza caracteres con problemas de encoding que vienen de la base de datos
     * Convierte: ? → ó, á, é, í, ú (según el contexto)
     */
    private normalizarEncoding(texto: string | undefined): string {
        if (!texto) return '';

        return texto
            .replace(/Documentaci\?n/gi, 'Documentación')
            .replace(/Legalizaci\?n/gi, 'Legalización')
            .replace(/Operaci\?n/gi, 'Operación')
            .replace(/Revisi\?n/gi, 'Revisión')
            .replace(/Inspecci\?n/gi, 'Inspección')
            .replace(/Tramitaci\?n/gi, 'Tramitación');
    }

    /**
     * Exportar a Excel (futuro)
     */
    exportarExcel(filters: ControlDusFilter): Observable<Blob> {
        let params = new HttpParams();

        // Agregar los mismos filtros que en getControlDusList
        if (filters.clienteId) {
            params = params.set('clienteId', filters.clienteId.toString());
        }
        if (filters.nave) {
            params = params.set('nave', filters.nave);
        }
        if (filters.dateFrom) {
            params = params.set('dateFrom', filters.dateFrom);
        }
        if (filters.dateTo) {
            params = params.set('dateTo', filters.dateTo);
        }

        return this.http.get(`${this.apiUrl}/export/excel`, {
            params,
            responseType: 'blob',
        });
    }

    /**
     * Obtener lista de documentos PDF asociados a un DUS
     * @param nroIE Número de instrucción de embarque (ID del DUS)
     * @param containerNumber Número de contenedor para filtrar documentos
     */
    getDocumentosPdfControlDus(
        nroIE: number,
        containerNumber?: string
    ): Observable<any> {
        let params = new HttpParams();
        if (containerNumber) {
            params = params.set('containerNumber', containerNumber);
        }
        return this.http.get(`${this.apiUrl}/${nroIE}/documents`, { params });
    }

    /**
     * Descargar un documento específico de un DUS (PDF, Excel, etc.).
     * @param nroIE Número de instrucción de embarque (ID del DUS)
     * @param fileId ID del archivo a descargar
     */
    descargarDocumentoPdf(nroIE: number, fileId: number): Observable<Blob> {
        return this.http.get(
            `${this.apiUrl}/${nroIE}/documents/${fileId}/download`,
            {
                responseType: 'blob',
                headers: new HttpHeaders({
                    Accept: [
                        'application/pdf',
                        'application/vnd.ms-excel',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'application/octet-stream',
                    ].join(', '),
                }),
            }
        );
    }

    /**
     * Descargar todos los documentos PDF de un DUS en un solo archivo
     * @param nroIE Número de instrucción de embarque (ID del DUS)
     */
    /**
     * Descargar documentos PDF seleccionados de un DUS en un solo archivo
     * @param nroIE Número de instrucción de embarque (ID del DUS)
     * @param fileIds IDs de los archivos a fusionar
     */
    descargarDocumentosSeleccionados(
        nroIE: number,
        fileIds: number[]
    ): Observable<Blob> {
        return this.http
            .post(
                `${this.apiUrl}/${nroIE}/documents/merge-selected`,
                { fileIds },
                {
                    responseType: 'blob',
                    observe: 'response',
                    headers: new HttpHeaders({
                        Accept: 'application/pdf',
                        'Content-Type': 'application/json',
                    }),
                }
            )
            .pipe(
                map((response) => {
                    const contentType =
                        response.headers.get('content-type') || '';

                    if (contentType.includes('application/json')) {
                        throw { isJsonError: true, blob: response.body };
                    }

                    if (!response.body) {
                        throw new Error(
                            'El archivo PDF consolidado está vacío'
                        );
                    }

                    return response.body;
                }),
                catchError((error) => {
                    if (error.isJsonError && error.blob instanceof Blob) {
                        return new Observable<Blob>((observer) => {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                try {
                                    const text = reader.result as string;
                                    const json = JSON.parse(text);
                                    const errorMessage = new Error(
                                        json.message || 'Error al procesar PDFs'
                                    );
                                    (errorMessage as any).errorJson = json;
                                    observer.error(errorMessage);
                                } catch {
                                    observer.error(
                                        new Error(
                                            'Error desconocido al procesar PDFs'
                                        )
                                    );
                                }
                            };
                            reader.onerror = () => {
                                observer.error(
                                    new Error(
                                        'Error al leer la respuesta del servidor'
                                    )
                                );
                            };
                            reader.readAsText(error.blob);
                        });
                    }

                    if (error.error instanceof Blob) {
                        return new Observable<Blob>((observer) => {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                try {
                                    const text = reader.result as string;
                                    const json = JSON.parse(text);
                                    const errorMessage = new Error(
                                        json.message || 'Error al procesar PDFs'
                                    );
                                    (errorMessage as any).errorJson = json;
                                    observer.error(errorMessage);
                                } catch {
                                    observer.error(
                                        new Error(
                                            'Error al descargar el PDF consolidado'
                                        )
                                    );
                                }
                            };
                            reader.onerror = () => {
                                observer.error(
                                    new Error(
                                        'Error al leer la respuesta del servidor'
                                    )
                                );
                            };
                            reader.readAsText(error.error);
                        });
                    }

                    return throwError(() => error);
                })
            );
    }

    /**
     * Obtener reporte de guías de despacho filtrado (para el listado)
     */
    getDispatcherReport(filters: ControlDusFilter): Observable<DispatchGuideReportItem[]> {
        let params = new HttpParams();
        if (filters.clienteId) params = params.set('clienteId', filters.clienteId.toString());
        if (filters.temporadaId) params = params.set('temporadaId', filters.temporadaId.toString());
        if (filters.nave) params = params.set('nave', filters.nave);
        if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
        if (filters.dateTo) params = params.set('dateTo', filters.dateTo);
        if (filters.referenciaCliente) params = params.set('referenciaCliente', filters.referenciaCliente);
        if (filters.contenedor) params = params.set('contenedor', filters.contenedor);
        if (filters.dus) params = params.set('dus', filters.dus);
        if (filters.booking) params = params.set('booking', filters.booking);
        if (filters.etapa) params = params.set('etapa', filters.etapa);
        if (filters.estado) params = params.set('estado', filters.estado);
        return this.http.get<DispatchGuideReportItem[]>(
            `${this.apiUrl}/dispatcher-report`,
            { params }
        );
    }

    /**
     * Obtener reporte de guías de despacho para un DUS específico (para el detalle)
     */
    getDispatcherReportByNroIE(nroIE: number): Observable<DispatchGuideReportItem[]> {
        return this.http.get<DispatchGuideReportItem[]>(
            `${this.apiUrl}/${nroIE}/dispatcher-report`
        );
    }

    descargarTodosDocumentosPdf(nroIE: number): Observable<Blob> {
        return this.http
            .get(`${this.apiUrl}/${nroIE}/documents/merge-download`, {
                responseType: 'blob',
                observe: 'response',
                headers: new HttpHeaders({
                    Accept: 'application/pdf',
                }),
            })
            .pipe(
                map((response) => {
                    // Verificar el Content-Type de la respuesta
                    const contentType =
                        response.headers.get('content-type') || '';

                    // Si el Content-Type es JSON, significa que hay un error del backend
                    if (contentType.includes('application/json')) {
                        // Lanzar error para que se maneje en catchError
                        throw { isJsonError: true, blob: response.body };
                    }

                    // Si es PDF, retornar el blob
                    if (!response.body) {
                        throw new Error(
                            'El archivo PDF consolidado está vacío'
                        );
                    }

                    return response.body;
                }),
                catchError((error) => {
                    // Si es un error JSON que viene como Blob
                    if (error.isJsonError && error.blob instanceof Blob) {
                        return new Observable<Blob>((observer) => {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                try {
                                    const text = reader.result as string;
                                    const json = JSON.parse(text);
                                    // Si es JSON, es un error del backend
                                    const errorMessage = new Error(
                                        json.message || 'Error al procesar PDFs'
                                    );
                                    (errorMessage as any).errorJson = json;
                                    observer.error(errorMessage);
                                } catch {
                                    observer.error(
                                        new Error(
                                            'Error desconocido al procesar PDFs'
                                        )
                                    );
                                }
                            };
                            reader.onerror = () => {
                                observer.error(
                                    new Error(
                                        'Error al leer la respuesta del servidor'
                                    )
                                );
                            };
                            reader.readAsText(error.blob);
                        });
                    }

                    // Si el error HTTP tiene un error.error que es Blob (respuesta HTTP con error pero content-type blob)
                    if (error.error instanceof Blob) {
                        return new Observable<Blob>((observer) => {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                try {
                                    const text = reader.result as string;
                                    const json = JSON.parse(text);
                                    // Si es JSON, es un error del backend
                                    const errorMessage = new Error(
                                        json.message || 'Error al procesar PDFs'
                                    );
                                    (errorMessage as any).errorJson = json;
                                    observer.error(errorMessage);
                                } catch {
                                    observer.error(
                                        new Error(
                                            'Error al descargar el PDF consolidado'
                                        )
                                    );
                                }
                            };
                            reader.onerror = () => {
                                observer.error(
                                    new Error(
                                        'Error al leer la respuesta del servidor'
                                    )
                                );
                            };
                            reader.readAsText(error.error);
                        });
                    }

                    // Si es un error HTTP normal, propagarlo
                    return throwError(() => error);
                })
            );
    }
}
